<template>
	<nav
		:aria-label="ariaLabelPaginationNav"
		role="navigation"
		class="pagination"
		v-show="pages > 1"
	>
		<button
			class="pagination__arrow pagination__prev"
			:class="{ 'pagination__arrow--disabled': activePage == 1 }"
			:aria-disabled="activePage == 1"
			@click="gotoPage(activePage - 1)"
			:aria-label="ariaLabelPaginationPrev"
		>
			<span class="icon icon-arrow">
				<svg
					version="1.1"
					width="16px"
					height="16px"
					viewBox="0 0 16 16"
					xmlns="http://www.w3.org/2000/svg"
				>
					<polygon
						transform="translate(-4,-4)"
						points="4 11 4 13 16.17 13 10.59 18.59 12 20 20 12 12 4 10.59 5.41 16.17 11"
					/>
				</svg>
			</span>
		</button>
		<ul class="button-list ul--inline ul--nostyling">
			<template v-for="page in pages">
				<li :key="page" v-if="showPage(page)">
					<button
						class="button--bordered button--small"
						:class="{ 'button--active': isActivePage(page) }"
						:aria-current="isActivePage(page)"
						:aria-label="
							isActivePage(page)
								? ariaLabelPaginationActivePage +
								  ', ' +
								  ariaLabelPaginationGotoPage +
								  ' ' +
								  page
								: ariaLabelPaginationGotoPage + ' ' + page
						"
						@click="gotoPage(page)"
					>
						{{ page }}
					</button>
				</li>
			</template>
		</ul>
		<button
			class="pagination__arrow pagination__next"
			:class="{ 'pagination__arrow--disabled': activePage == pages }"
			@click="gotoPage(activePage + 1)"
			:aria-label="ariaLabelPaginationNext"
			:aria-disabled="activePage == pages"
		>
			<span class="icon icon-arrow">
				<svg
					version="1.1"
					width="16px"
					height="16px"
					viewBox="0 0 16 16"
					xmlns="http://www.w3.org/2000/svg"
				>
					<polygon
						transform="translate(-4,-4)"
						points="4 11 4 13 16.17 13 10.59 18.59 12 20 20 12 12 4 10.59 5.41 16.17 11"
					/>
				</svg>
			</span>
		</button>
	</nav>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  pageSize: {
    type: Number,
    default: 10
  },
  totalItems: {
    type: Number,
    default: 0
  },
  currentPage: {
    type: Number,
    default: 0
  },
  ariaLabelPaginationGotoPage: {
    type: String,
    default: "Gå til side"
  },
  ariaLabelPaginationNav: {
    type: String,
    default: "Paginerings navigation"
  },
  ariaLabelPaginationNext: {
    type: String,
    default: "Næste"
  },
  ariaLabelPaginationPrev: {
    type: String,
    default: "Forrige"
  },
  ariaLabelPaginationActivePage: {
    type: String,
    default: "Aktiv side"
  }
});

const emit = defineEmits(['changePageIndex']);

const gotoPage = (index) => {
  emit('changePageIndex', index - 1);
  setTimeout(() => {
    const y =
      document.documentElement.getBoundingClientRect().top +
	  window.scrollY +
      -250;

    window.scrollTo({ top: y, behavior: 'smooth' });
  }, 500);
};

const isActivePage = (index) => {
  return activePage.value === index;
};

const showPage = (index) => {
  let visiblePagesAfterCurrentPage =
    index - Math.floor(visiblePages.value / 2);

  if (Math.floor(visiblePages.value / 2) - props.currentPage > 0) {
    visiblePagesAfterCurrentPage -=
      Math.floor(visiblePages.value / 2) - props.currentPage;
  }

  let visiblePagesBeforeCurrentPage =
    index + Math.ceil(visiblePages.value / 2);
  if (
    Math.ceil(visiblePages.value / 2) + props.currentPage >
    pages.value
  ) {
    visiblePagesBeforeCurrentPage -=
      pages.value -
      (Math.ceil(visiblePages.value / 2) + props.currentPage);
  }

  return (
    (visiblePagesBeforeCurrentPage > activePage.value &&
      index <= activePage.value) ||
    (visiblePagesAfterCurrentPage <= activePage.value &&
      index > activePage.value)
  );
};

const visiblePages = computed(() => {
  return screen.width <= 760 ? 3 : 5;
});

const pages = computed(() => {
  let pages = Math.ceil(props.totalItems / props.pageSize);
  if (Number.isInteger(pages)) {
    return Math.ceil(props.totalItems / props.pageSize);
  } else {
    return 0;
  }
});

const activePage = computed(() => {
  return props.currentPage + 1;
});
</script>